import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import NoAccess from "./pages/NoAccess";
import Layout from "./components/Layout";

// Lazy-loaded pages
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const BOQManagement = lazy(() => import("./pages/BOQManagement"));
const ProgressTracking = lazy(() => import("./pages/ProgressTracking"));
const Billing = lazy(() => import("./pages/Billing"));
const BillingOverview = lazy(() => import("./pages/BillingOverview"));
const Reports = lazy(() => import("./pages/Reports"));
const Users = lazy(() => import("./pages/Users"));
const GanttChart = lazy(() => import("./pages/GanttChart"));
const RoleManagement = lazy(() => import("./pages/RoleManagement"));
const AdminExpenses = lazy(() => import("./pages/AdminExpenses"));
const ProjectExpenses = lazy(() => import("./pages/ProjectExpenses"));
const AddBilling = lazy(() => import("./pages/AddBilling"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const Licenses = lazy(() => import("./pages/Licenses"));

// Attach stored session token as Bearer header fallback (for mobile browsers that block cross-site cookies)
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("session_token");
  if (token && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--ring))]"></div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Safe permission check helper - prevents white screen on undefined permissions
export const hasPermission = (user, module, action = "view") => {
  return user?.permissions?.[module]?.[action] === true;
};

// Helper function to get default page based on user role
const getDefaultPageForRole = (role) => {
  switch (role) {
    case "Engineer":
      return "/projects";
    case "Accountant":
      return "/billing";
    default:
      return "/dashboard";
  }
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        // Store session token for mobile browsers that block cross-site cookies
        if (response.data?.session_token) {
          sessionStorage.setItem("session_token", response.data.session_token);
        }

        const defaultPage = getDefaultPageForRole(response.data.role);
        toast.success("Login successful!");
        navigate(defaultPage, { state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--ring))] mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Pending Approval Screen for unapproved users
const PendingApprovalScreen = ({ user, onLogout }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="max-w-md w-full text-center space-y-6">
      <div className="w-20 h-20 mx-auto bg-amber-500/10 rounded-full flex items-center justify-center">
        <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Account Pending Approval</h1>
        <p className="text-muted-foreground">
          Your account is awaiting administrator approval. You will receive access once an admin approves your account.
        </p>
      </div>
      <div className="bg-muted/50 rounded-lg p-4 text-left">
        <p className="text-sm text-muted-foreground mb-1">Signed in as:</p>
        <p className="font-medium text-foreground">{user?.name || user?.email}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
      </div>
      <button
        onClick={onLogout}
        className="w-full py-2 px-4 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors"
      >
        Sign out
      </button>
      <p className="text-xs text-muted-foreground">
        If you believe this is an error, please contact your system administrator.
      </p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [authState, setAuthState] = useState("loading");
  const [user, setUser] = useState(null);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    sessionStorage.removeItem("session_token");
    navigate("/login");
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setAuthState("authenticated");
      } catch (error) {
        setAuthState("unauthenticated");
      }
    };
    checkAuth();
  }, []);

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--ring))]"></div>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user && user.is_approved === false) {
    return <PendingApprovalScreen user={user} onLogout={handleLogout} />;
  }

  return <Layout user={user}>{children}</Layout>;
};

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/projects/:projectId/boq" element={<ProtectedRoute><BOQManagement /></ProtectedRoute>} />
        <Route path="/projects/:projectId/progress" element={<ProtectedRoute><ProgressTracking /></ProtectedRoute>} />
        <Route path="/projects/:projectId/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/gantt" element={<ProtectedRoute><GanttChart /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingOverview /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/roles" element={<ProtectedRoute><RoleManagement /></ProtectedRoute>} />
        <Route path="/admin-expenses" element={<ProtectedRoute><AdminExpenses /></ProtectedRoute>} />
        <Route path="/project-expenses" element={<ProtectedRoute><ProjectExpenses /></ProtectedRoute>} />
        <Route path="/add-billing" element={<ProtectedRoute><AddBilling /></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
        <Route path="/licenses" element={<ProtectedRoute><Licenses /></ProtectedRoute>} />
        <Route path="/no-access" element={<ProtectedRoute><NoAccess /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;